/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/sendInvoice.scss';

import {AwesomeIcon, ChangeFunction, CloseButton, ErrorType, Loader, WithBackground} from '@dvrd/dvr-controls';
import React, {MouseEventHandler, useContext, useEffect, useRef, useState} from 'react';
import EmailEditor, {EditorRef} from 'react-email-editor';
import {getMergeTags} from "../../../util/unlayerUtil";
import {AppContext} from "../../misc/appContext";
import {BolmateButton, BolmateInput} from "../../controls";
import Invoice from "../../../models/invoiceModel";
import {UnlayerDesign} from "../../../util/interfaces";

interface Props {
    onClose: MouseEventHandler;
    onSubmit: (data: { design: UnlayerDesign, html: string }) => void;
    onChangeSubject: ChangeFunction;
    active: boolean;
    subject: string;
    loading: boolean;
    invoice: Invoice | null;
    error: ErrorType;
}

export default function SendInvoice(props: Props) {
    const context = useContext(AppContext);
    const {active, onChangeSubject, subject, onSubmit, onClose, loading, invoice, error} = props;
    const editorRef = useRef<EditorRef>(null);
    const [loadingEditor, setLoadingEditor] = useState(true);

    function onEditorLoaded() {
        setLoadingEditor(false);
    }

    function _onSubmit() {
        if (!editorRef.current) return;
        editorRef.current?.editor?.exportHtml((data: { design: UnlayerDesign, html: string }) => {
            onSubmit(data);
        });
    }

    function setDesign() {
        if (!editorRef.current) return;
        let design: UnlayerDesign | null = getDesignFromAccount();
        if (!design)
            design = require('../../../../res/static/emailTemplates/invoice-simple.json');
        editorRef.current?.editor?.loadDesign(design!)
        editorRef.current?.editor?.setMergeTags(getMergeTags(context.accountContext.account));
    }

    function getDesignFromAccount(): UnlayerDesign | null {
        const manualMailJson = context.accountContext.account?.settings?.manualMailJson;
        if (!manualMailJson || !Object.keys(manualMailJson).length) return null;
        return manualMailJson;
    }

    function renderBody() {
        return (
            <div className='body-container'>
                <Loader active={loading}/>
                {renderInvoiceDetails()}
                <div className='email-container'>
                    <BolmateInput onChange={onChangeSubject} value={subject} label='Onderwerp *'
                                  inputProps={{placeholder: 'Onderwerp van de e-mail'}}/>
                    <div>
                        <EmailEditor ref={editorRef} onReady={onEditorLoaded} projectId={43580} minHeight={400}
                                     options={{
                                         features: {
                                             // @ts-ignore
                                             smartMergeTags: false
                                         }
                                     }}/>
                    </div>
                </div>
                <label className='attachment-label'><AwesomeIcon
                    name='paperclip'/> <span className='bold'>Bijlage:</span> {invoice?.orderId}.pdf</label>
                <label className='error'>{error}</label>
            </div>
        )
    }

    function renderInvoiceDetails() {
        return (
            <div className='invoice-details'>
                <div className='field'>
                    <label className='key'>Ordernummer:</label>
                    <label>{invoice?.orderId}</label>
                </div>
                <div className='field'>
                    <label className='key'>Factuurnummer:</label>
                    <label>{invoice?.invoiceNum}</label>
                </div>
                <div className='field'>
                    <label className='key'>Factuurdatum:</label>
                    <label>{invoice?.invoiceDate.format('DD-MM-YYYY')}</label>
                </div>
                <div className='field'>
                    <label className='key'>Klant:</label>
                    <label>{invoice?.invoiceName}</label>
                </div>
            </div>
        );
    }

    useEffect(() => {
        if (!loadingEditor && active) setDesign();
        if (!active) setLoadingEditor(true);
    }, [loadingEditor, active]);

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='send-invoice-popup default-popup'>
                <div className='header-container'>
                    <label className='header-title'>Factuur verzenden</label>
                    <CloseButton onClick={onClose}/>
                </div>
                {renderBody()}
                <div className='footer-container'>
                    <label className='disclaimer'>Voor deze e-mail worden niet de instellingen van de E-mails pagina
                        gebruikt.</label>
                    <div className='buttons-container'>
                        <BolmateButton onClick={onClose} label='Annuleren' disabled={loading} secondary/>
                        <BolmateButton onClick={_onSubmit} label='Verzenden' disabled={loading || loadingEditor}/>
                    </div>
                </div>
            </div>
        </WithBackground>
    )
}